import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { updateProfile, updatePublicProfileSetting, getReminderPreferences, updateReminderPreferences } from '../../lib/supabase'
import { User, Lock, Bell, CheckCircle, AlertCircle, Camera, Share2, Copy } from 'lucide-react'

export default function Settings() {
  const { user, profile, refreshProfile, updatePassword } = useAuth()

  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [companyName, setCompanyName] = useState('')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [publicProfile, setPublicProfile] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)

  // Notification preferences
  const [emailRemindersEnabled, setEmailRemindersEnabled] = useState(true)
  const [registrationReminderDays, setRegistrationReminderDays] = useState(30)
  const [insuranceReminderDays, setInsuranceReminderDays] = useState(30)
  const [serviceReminderMonths, setServiceReminderMonths] = useState(6)

  const [profileLoading, setProfileLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [publicProfileLoading, setPublicProfileLoading] = useState(false)
  const [reminderLoading, setReminderLoading] = useState(false)
  const [profileSuccess, setProfileSuccess] = useState(false)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [publicProfileSuccess, setPublicProfileSuccess] = useState(false)
  const [reminderSuccess, setReminderSuccess] = useState(false)
  const [error, setError] = useState('')

  // Load profile data
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '')
      setPhone(profile.phone || '')
      setCompanyName(profile.company_name || '')
      setPublicProfile(profile.public_profile || false)
    }
  }, [profile])

  // Load reminder preferences
  useEffect(() => {
    if (user?.id) {
      loadReminderPreferences()
    }
  }, [user?.id])

  const loadReminderPreferences = async () => {
    try {
      const { data, error } = await getReminderPreferences(user.id)
      if (error) throw error

      if (data) {
        setEmailRemindersEnabled(data.email_reminders ?? data.email_enabled ?? true)
        setRegistrationReminderDays(data.registration_reminder_days ?? 30)
        setInsuranceReminderDays(data.insurance_reminder_days ?? 30)
        setServiceReminderMonths(data.service_reminder_months ?? 6)
      }
    } catch (err) {
      console.error('Failed to load reminder preferences:', err)
    }
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    setError('')
    setProfileSuccess(false)
    setProfileLoading(true)

    try {
      const { error } = await updateProfile(user.id, {
        full_name: fullName,
        phone: phone,
        company_name: companyName
      })

      if (error) throw error

      await refreshProfile()
      setProfileSuccess(true)
      setTimeout(() => setProfileSuccess(false), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setProfileLoading(false)
    }
  }

  const handlePasswordUpdate = async (e) => {
    e.preventDefault()
    setError('')
    setPasswordSuccess(false)

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setPasswordLoading(true)

    try {
      const { error } = await updatePassword(newPassword)

      if (error) throw error

      setNewPassword('')
      setConfirmPassword('')
      setPasswordSuccess(true)
      setTimeout(() => setPasswordSuccess(false), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handlePublicProfileToggle = async () => {
    setError('')
    setPublicProfileLoading(true)
    setPublicProfileSuccess(false)

    try {
      const newValue = !publicProfile
      const { error: updateError } = await updatePublicProfileSetting(user.id, newValue)

      if (updateError) throw updateError

      setPublicProfile(newValue)
      await refreshProfile()
      setPublicProfileSuccess(true)
      setTimeout(() => setPublicProfileSuccess(false), 3000)
    } catch (err) {
      console.error('Error updating public profile:', err)
      setError(err?.message || 'Failed to update sharing settings. Please try again.')
    } finally {
      setPublicProfileLoading(false)
    }
  }

  const getShareLink = () => {
    return `${window.location.origin}/g/${user?.id}`
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareLink())
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  const handleReminderUpdate = async () => {
    setError('')
    setReminderSuccess(false)
    setReminderLoading(true)

    try {
      const { error } = await updateReminderPreferences(user.id, {
        email_reminders: emailRemindersEnabled,
        email_enabled: emailRemindersEnabled,
        registration_reminder_days: registrationReminderDays,
        insurance_reminder_days: insuranceReminderDays,
        service_reminder_months: serviceReminderMonths
      })

      if (error) throw error

      setReminderSuccess(true)
      setTimeout(() => setReminderSuccess(false), 3000)
    } catch (err) {
      setError(err.message || 'Failed to update reminder preferences')
    } finally {
      setReminderLoading(false)
    }
  }

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="mb-8">
        <h1 className="page-title">Settings</h1>
        <p className="text-rdc-taupe mt-1">
          Manage your account and preferences
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-sm underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Profile Section */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center">
            <User size={20} className="text-rdc-primary" />
          </div>
          <div>
            <h2 className="section-title">Profile Information</h2>
            <p className="text-sm text-rdc-taupe">Update your personal details</p>
          </div>
        </div>

        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input-field"
                placeholder="Your full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-field"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
              Company / Organization
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="input-field"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
              Email
            </label>
            <input
              type="email"
              value={user?.email || ''}
              className="input-field bg-rdc-cream"
              disabled
            />
            <p className="text-xs text-rdc-taupe mt-1">
              Contact support to change your email address
            </p>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={profileLoading}
              className="btn-primary"
            >
              {profileLoading ? 'Saving...' : 'Save Changes'}
            </button>
            {profileSuccess && (
              <span className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle size={16} />
                Saved successfully
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Password Section */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center">
            <Lock size={20} className="text-rdc-primary" />
          </div>
          <div>
            <h2 className="section-title">Change Password</h2>
            <p className="text-sm text-rdc-taupe">Update your account password</p>
          </div>
        </div>

        <form onSubmit={handlePasswordUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                minLength={8}
                required
              />
              <p className="text-xs text-rdc-taupe mt-1">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={passwordLoading}
              className="btn-primary"
            >
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
            {passwordSuccess && (
              <span className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle size={16} />
                Password updated
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Account Info */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-rdc-cream flex items-center justify-center">
            <Bell size={20} className="text-rdc-taupe" />
          </div>
          <div>
            <h2 className="section-title">Account Information</h2>
            <p className="text-sm text-rdc-taupe">Your membership details</p>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-rdc-cream">
            <span className="text-rdc-taupe">Member Since</span>
            <span className="text-black font-medium">
              {profile?.member_since || profile?.created_at
                ? new Date(profile.member_since || profile.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })
                : '—'}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-rdc-cream">
            <span className="text-rdc-taupe">Account Type</span>
            <span className="text-black font-medium capitalize">{profile?.role || 'Client'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-rdc-taupe">Account Status</span>
            <span className="text-green-600 font-medium">Active</span>
          </div>
        </div>
      </div>

      {/* Notification Preferences Section */}
      <div className="card p-6 mt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center">
            <Bell size={20} className="text-rdc-primary" />
          </div>
          <div>
            <h2 className="section-title">Notification Preferences</h2>
            <p className="text-sm text-rdc-taupe">Manage email reminders for vehicle maintenance</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Email Reminders Toggle */}
          <div className="flex items-center justify-between p-4 bg-rdc-cream rounded-lg">
            <div>
              <p className="text-sm font-medium text-black">Email Reminders</p>
              <p className="text-xs text-rdc-taupe mt-1">
                {emailRemindersEnabled
                  ? 'Receive email reminders for vehicle expirations'
                  : 'Email reminders disabled'}
              </p>
            </div>
            <button
              onClick={() => setEmailRemindersEnabled(!emailRemindersEnabled)}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                emailRemindersEnabled
                  ? 'bg-rdc-primary'
                  : 'bg-rdc-warm-gray'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  emailRemindersEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Reminder Timing Settings */}
          {emailRemindersEnabled && (
            <div className="space-y-4 p-4 bg-rdc-cream/50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                    Registration Reminder
                  </label>
                  <select
                    value={registrationReminderDays}
                    onChange={(e) => setRegistrationReminderDays(Number(e.target.value))}
                    className="input-field"
                  >
                    <option value={7}>7 days before</option>
                    <option value={14}>14 days before</option>
                    <option value={30}>30 days before</option>
                    <option value={60}>60 days before</option>
                  </select>
                  <p className="text-xs text-rdc-taupe mt-1">
                    When your vehicle registration expires
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                    Insurance Reminder
                  </label>
                  <select
                    value={insuranceReminderDays}
                    onChange={(e) => setInsuranceReminderDays(Number(e.target.value))}
                    className="input-field"
                  >
                    <option value={7}>7 days before</option>
                    <option value={14}>14 days before</option>
                    <option value={30}>30 days before</option>
                    <option value={60}>60 days before</option>
                  </select>
                  <p className="text-xs text-rdc-taupe mt-1">
                    When your insurance expires
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Service Reminder
                </label>
                <select
                  value={serviceReminderMonths}
                  onChange={(e) => setServiceReminderMonths(Number(e.target.value))}
                  className="input-field"
                >
                  <option value={3}>Every 3 months</option>
                  <option value={6}>Every 6 months</option>
                  <option value={12}>Every 12 months</option>
                </select>
                <p className="text-xs text-rdc-taupe mt-1">
                  Reminder frequency for regular vehicle service
                </p>
              </div>

              <div className="flex items-center gap-4 pt-4 border-t border-rdc-cream">
                <button
                  onClick={handleReminderUpdate}
                  disabled={reminderLoading}
                  className="btn-primary"
                >
                  {reminderLoading ? 'Saving...' : 'Save Preferences'}
                </button>
                {reminderSuccess && (
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle size={16} />
                    Preferences saved
                  </span>
                )}
              </div>
            </div>
          )}

          {!emailRemindersEnabled && (
            <div className="p-4 bg-rdc-cream/50 rounded-lg text-xs text-rdc-taupe">
              <p>Enable email reminders above to configure your notification preferences.</p>
            </div>
          )}
        </div>
      </div>

      {/* Share My Garage Section */}
      <div className="card p-6 mt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-rdc-primary/10 flex items-center justify-center">
            <Share2 size={20} className="text-rdc-primary" />
          </div>
          <div>
            <h2 className="section-title">Share My Garage</h2>
            <p className="text-sm text-rdc-taupe">Make your vehicle collection publicly viewable</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-rdc-cream rounded-lg">
            <div>
              <p className="text-sm font-medium text-black">Public Garage Profile</p>
              <p className="text-xs text-rdc-taupe mt-1">
                {publicProfile
                  ? 'Your garage is visible to anyone with the link'
                  : 'Enable to share your garage with others'}
              </p>
              {publicProfileSuccess && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle size={12} />
                  {publicProfile ? 'Sharing enabled!' : 'Sharing disabled'}
                </p>
              )}
            </div>
            <button
              onClick={handlePublicProfileToggle}
              disabled={publicProfileLoading}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors disabled:opacity-50 ${
                publicProfile
                  ? 'bg-rdc-primary'
                  : 'bg-rdc-warm-gray'
              }`}
            >
              {publicProfileLoading ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                </span>
              ) : (
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                    publicProfile ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              )}
            </button>
          </div>

          {publicProfile && (
            <div className="p-4 bg-rdc-cream rounded-lg border border-rdc-primary/20">
              <p className="text-xs font-medium text-rdc-dark-gray mb-3">Share this link:</p>
              <div className="flex items-center gap-2 bg-white rounded-lg p-3 border border-rdc-cream">
                <code className="text-xs font-mono text-rdc-taupe flex-1 break-all">
                  {getShareLink()}
                </code>
                <button
                  onClick={handleCopyLink}
                  className="flex-shrink-0 p-2 hover:bg-rdc-cream rounded-lg transition-colors"
                  title="Copy link"
                >
                  <Copy size={16} className={copiedLink ? 'text-rdc-primary' : 'text-rdc-dark-gray'} />
                </button>
              </div>
              {copiedLink && (
                <p className="text-xs text-rdc-primary mt-2 flex items-center gap-1">
                  <CheckCircle size={14} />
                  Copied to clipboard!
                </p>
              )}
            </div>
          )}

          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-xs text-rdc-dark-gray">
            <p className="font-medium text-amber-800 mb-1">Per-vehicle control</p>
            <p className="text-rdc-taupe">
              Even with sharing enabled, only vehicles you've marked as public will appear.
              Use the eye icon on each vehicle card in your garage to control which cars are visible.
            </p>
          </div>

          <div className="p-4 bg-rdc-cream/50 rounded-lg text-xs text-rdc-taupe">
            <p className="mb-2 font-medium">What's shared:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Your name and member date</li>
              <li>Vehicle photos, year, make, and model</li>
              <li>Basic vehicle specifications</li>
              <li>Vehicle descriptions</li>
            </ul>
            <p className="mt-2 text-xs text-rdc-taupe font-medium">What's private:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Service history and maintenance records</li>
              <li>Documents and appointments</li>
              <li>VIN and specific location details</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
